/**
 * Rulebook — living reference + the app's own punch list.
 *
 * Layers per feature: Plain English / The Data / The Rules / The Code.
 * Below the layers: that feature's Fixes & Additions list (home items +
 * items where this feature appears in also_affects). Aggregate tab pools
 * every worklist item.
 *
 * Content: /app/frontend/src/data/rulebookData.json (pure data, diffable).
 * Storage: db.app_worklist_items (tenant-scoped).
 * Access: any authenticated admin. Verify status: super-admin only.
 *
 * DESIGN PASS 4: this is the first page converted onto the token system.
 * Every colour, type size, family, shadow, radius and duration on this
 * page comes from keymade-tokens.css via styles/pages/rulebook.css.
 * Tailwind is used ONLY for layout and spacing. The file is OFF the
 * design-token allowlist, so the guard now enforces it.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, BookOpen, Database, AlertTriangle, ArrowLeft,
  FileCode, ChevronRight, ShieldCheck, ListChecks, ClipboardCopy,
  Map as MapIcon, ChevronDown,
} from 'lucide-react';
import rulebookData from '@/data/rulebookData.json';
import rulebookMap from '@/data/rulebookMap.json';
import { PROJECT_NAV_SECTIONS } from '@/utils/projectNavManifest';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import WorklistCaptureForm from '@/components/rulebook/WorklistCaptureForm';
import WorklistItem, { REMAINING_STATUSES, COMPLETED_STATUSES } from '@/components/rulebook/WorklistItem';
import '@/styles/pages/rulebook.css';

// Publish the feature list to the window so the WorklistItem edit form
// can read it without re-importing rulebookData (keeps the component
// light and avoids a circular data import).
if (typeof window !== 'undefined') {
  window.__rulebookFeatures = rulebookData.features.map(f => ({ id: f.id, name: f.name }));
}

/* DP278-C6a — the column's six groups became nine bands in job order. A map
 * node still stores one of the old six titles, so each resolves to the bands
 * that absorbed it; a node that names a band by id or title resolves directly. */
const MANIFEST_SECTION_ALIASES = {
  'The Desk': ['desk'],
  'Project Desk': ['desk'],
  'The Work': ['plans', 'design'],
  'The Trades': ['build', 'price'],
  Money: ['price', 'money'],
  Communications: ['talk', 'setup'],
  'The File': ['setup', 'close'],
};

const manifestSectionItems = (key) => {
  const ids = MANIFEST_SECTION_ALIASES[key] || [key];
  return PROJECT_NAV_SECTIONS
    .filter(s => ids.includes(s.id) || s.id === key || s.title === key)
    .flatMap(s => s.items);
};

/* Flatten the map tree for lookup by id, and for search.
 * Each entry gets `path[]` (breadcrumb chain of labels) computed on the fly. */
function flattenMap(topLevel) {
  const out = [];
  const walk = (node, path) => {
    const crumb = [...path, node.label];
    out.push({ ...node, path: crumb });
    (node.children || []).forEach(c => walk(c, crumb));
  };
  (topLevel || []).forEach(t => walk(t, []));
  return out;
}
const MAP_FLAT = flattenMap(rulebookMap.top_level);
const NODE_BY_ID = Object.fromEntries(MAP_FLAT.map(n => [n.id, n]));

function nodeMatches(node, needle) {
  if (!needle) return true;
  const n = needle.toLowerCase();
  const hay = [
    node.label, node.description, node.route,
    node.manifest_value, node.manifest_route, node.manifest_section,
    ...(node.path || []),
  ].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(n);
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function matches(feature, needle) {
  if (!needle) return true;
  const n = needle.toLowerCase();
  const hay = [
    feature.name, feature.plain_english,
    feature.data?.single_source_of_truth,
    ...(feature.data?.derivatives || []),
    ...(feature.data?.collections || []).flatMap(c => [c.name, c.role]),
    ...(feature.rules || []),
    ...(feature.code?.backend_routes || []).flatMap(r => [r.path, r.file]),
    ...(feature.code?.frontend_files || []),
  ].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(n);
}

function dataTypeMatches(dt, needle) {
  if (!needle) return true;
  const n = needle.toLowerCase();
  const hay = [dt.name, dt.description, ...(dt.collections || [])].join(' ').toLowerCase();
  return hay.includes(n);
}

function itemMatches(item, needle) {
  if (!needle) return true;
  const n = needle.toLowerCase();
  const hay = [
    item.title, item.description, item.priority, item.label,
    item.author_name, item.type, item.status, item.feature_id,
    ...(item.also_affects || []).map(a => a.feature_id + ' ' + (a.note || '')),
  ].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(n);
}

function isInvolvedIn(item, featureId) {
  if (item.feature_id === featureId) return true;
  return (item.also_affects || []).some(a => a.feature_id === featureId);
}

export default function Rulebook() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin = useIsSuperAdmin();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  // App Map is the DEFAULT tab per navigation-redesign build.
  const initialTab =
    searchParams.get('tab') ||
    (searchParams.get('feature') ? 'features'
      : searchParams.get('type') ? 'data-types'
      : 'app-map');
  const [tab, setTab] = useState(initialTab);
  const [selectedFeatureId, setSelectedFeatureId] = useState(searchParams.get('feature') || rulebookData.features[0].id);
  const [selectedTypeId, setSelectedTypeId] = useState(searchParams.get('type') || rulebookData.data_types[0].id);
  const [selectedNodeId, setSelectedNodeId] = useState(searchParams.get('node') || rulebookMap.top_level[0].id);
  // Which map branches are expanded — default: the first THREE levels open
  // so 3rd-level nodes like `projects.inside.quality-control` are visible.
  const [expanded, setExpanded] = useState(() => {
    const s = new Set();
    (rulebookMap.top_level || []).forEach(t => {
      s.add(t.id);
      (t.children || []).forEach(c => {
        s.add(c.id);
        (c.children || []).forEach(gc => s.add(gc.id));
      });
    });
    // If a deep node is deep-linked, walk up and expand its ancestors.
    const initNode = searchParams.get('node');
    if (initNode) {
      const parts = initNode.split('.');
      for (let i = 1; i <= parts.length; i++) s.add(parts.slice(0, i).join('.'));
    }
    return s;
  });
  const [aggFilter, setAggFilter] = useState({ status: 'open', featureId: '', blockingOnly: false });

  useEffect(() => {
    const p = new URLSearchParams();
    p.set('tab', tab);
    if (tab === 'features') p.set('feature', selectedFeatureId);
    if (tab === 'data-types') p.set('type', selectedTypeId);
    if (tab === 'app-map') p.set('node', selectedNodeId);
    setSearchParams(p, { replace: true });
  }, [tab, selectedFeatureId, selectedTypeId, selectedNodeId, setSearchParams]);

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const fetchItems = useCallback(async () => {
    try {
      setLoadingItems(true);
      const { data } = await axios.get(`${API}/rulebook/worklist`);
      setItems(data.items || []);
    } catch (e) {
      toast.error('Could not load worklist items.');
    } finally {
      setLoadingItems(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const featureById = useCallback(
    (id) => rulebookData.features.find(f => f.id === id) || null,
    [],
  );

  const filteredFeatures = useMemo(
    () => rulebookData.features.filter(f => matches(f, query)),
    [query],
  );
  const filteredTypes = useMemo(
    () => rulebookData.data_types.filter(t => dataTypeMatches(t, query)),
    [query],
  );

  // Badge = REMAINING items (open + prompted + built), not open-only: an item
  // moved to Prompted or Built is still outstanding work.
  const remainingItemsCountByFeature = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (!REMAINING_STATUSES.includes(it.status)) continue;
      for (const fid of new Set([it.feature_id, ...(it.also_affects || []).map(a => a.feature_id)])) {
        if (!fid) continue;
        map.set(fid, (map.get(fid) || 0) + 1);
      }
    }
    return map;
  }, [items]);

  // Per-node open-item badge = union of open items whose feature (home or
  // also_affects) is xref'd from any node or descendant.
  const remainingItemsCountByNode = useMemo(() => {
    const nodeToFeatures = new Map(); // node id → Set<feature id> (from itself + all descendants)
    const collect = (node) => {
      const set = new Set(node.feature_ids || []);
      (node.children || []).forEach(c => {
        const child = collect(c);
        child.forEach(f => set.add(f));
      });
      nodeToFeatures.set(node.id, set);
      return set;
    };
    (rulebookMap.top_level || []).forEach(collect);
    const out = new Map();
    for (const [nodeId, features] of nodeToFeatures.entries()) {
      let count = 0;
      for (const f of features) count += (remainingItemsCountByFeature.get(f) || 0);
      out.set(nodeId, count);
    }
    return out;
  }, [remainingItemsCountByFeature]);

  const selectedNode = NODE_BY_ID[selectedNodeId] || rulebookMap.top_level[0];
  const itemsForSelectedNode = useMemo(() => {
    const nodeFeatures = new Set(selectedNode?.feature_ids || []);
    if (nodeFeatures.size === 0) return [];
    return items.filter(it => {
      const involved = new Set([it.feature_id, ...(it.also_affects || []).map(a => a.feature_id)]);
      for (const f of nodeFeatures) if (involved.has(f)) return true;
      return false;
    });
  }, [items, selectedNode]);

  const selectedFeature = featureById(selectedFeatureId) || rulebookData.features[0];
  const selectedType = rulebookData.data_types.find(t => t.id === selectedTypeId) || rulebookData.data_types[0];

  const itemsForSelectedFeature = useMemo(
    () => items.filter(it => isInvolvedIn(it, selectedFeature.id)),
    [items, selectedFeature],
  );

  const aggregateItems = useMemo(() => {
    let list = items.slice();
    if (aggFilter.status && aggFilter.status !== 'all') list = list.filter(i => i.status === aggFilter.status);
    if (aggFilter.featureId) list = list.filter(i => isInvolvedIn(i, aggFilter.featureId));
    if (aggFilter.blockingOnly) list = list.filter(i => i.blocking);
    if (query) list = list.filter(i => itemMatches(i, query));
    // Blocking + open pinned at top
    list.sort((a, b) => {
      const aB = a.blocking && a.status === 'open' ? 1 : 0;
      const bB = b.blocking && b.status === 'open' ? 1 : 0;
      if (aB !== bB) return bB - aB;
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return list;
  }, [items, aggFilter, query]);

  const onItemCreated = (it) => setItems(prev => [it, ...prev]);
  const onItemChanged = (it) => setItems(prev => prev.map(x => x.id === it.id ? it : x));

  // Remaining = open + prompted + built. Completed = verified (Completed) +
  // withdrawn (Not doing). Computed from the full unfiltered list so the number
  // is "how many are left", not "how many rows the current filter returned".
  const remainingCount = useMemo(
    () => items.filter(i => REMAINING_STATUSES.includes(i.status)).length,
    [items],
  );
  const completedCount = useMemo(
    () => items.filter(i => COMPLETED_STATUSES.includes(i.status)).length,
    [items],
  );

  const seedItems = async () => {
    if (!isSuperAdmin) return;
    try {
      const { data } = await axios.post(`${API}/rulebook/worklist/seed`);
      toast.success(`Seed complete: ${data.created} created, ${data.already} already present.`);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed.');
    }
  };

  const copyOpenItems = async () => {
    const openList = aggregateItems.filter(i => i.status === 'open');
    const lines = openList.map(i => {
      const home = i.feature_id ? (featureById(i.feature_id)?.name || i.feature_id) : `(unassigned${i.label ? ' · ' + i.label : ''})`;
      const affects = (i.also_affects || []).map(a => featureById(a.feature_id)?.name || a.feature_id).filter(Boolean);
      const parts = [
        `[${i.priority || i.type.toUpperCase()}] ${home} — ${i.title}`,
        i.blocking ? '  ⚠ BLOCKING LIVE PROJECT' : null,
        i.description ? `  ${i.description}` : null,
        affects.length ? `  Also affects: ${affects.join(', ')}` : null,
        i.screenshot_url ? '  [screenshot attached]' : null,
      ].filter(Boolean);
      return parts.join('\n');
    });
    const text = `KEY-MADE Fixes & Additions — ${openList.length} open item(s)\n\n${lines.join('\n\n')}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${openList.length} open items.`);
    } catch (e) {
      toast.error('Clipboard not available.');
    }
  };

  const TABS = [
    { id: 'app-map', label: 'App Map', icon: MapIcon, count: (rulebookMap.top_level || []).length, testid: 'rulebook-tab-app-map' },
    { id: 'features', label: 'By System', icon: BookOpen, count: filteredFeatures.length, testid: 'rulebook-tab-features' },
    { id: 'data-types', label: 'Data Types', icon: Database, count: filteredTypes.length, testid: 'rulebook-tab-data-types' },
    { id: 'fixes-additions', label: 'Fixes & Additions', icon: AlertTriangle, count: remainingCount, testid: 'rulebook-tab-fixes-additions', countTestid: 'rulebook-tab-remaining-badge' },
  ];

  return (
    <div className="km-rulebook" data-testid="rulebook-page">
      <div className="km-rb-shell">
        <div className="km-topbar">
          <div className="flex items-center gap-3">
            <button className="km-btn km-btn--quiet" onClick={() => navigate('/admin')} data-testid="rulebook-back-to-admin">
              <ArrowLeft className="w-4 h-4" /> Admin
            </button>
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 km-mark" />
              <h1 className="km-title">The Rulebook</h1>
              <span className="km-tag" data-testid="rulebook-version">{rulebookData.version}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <button className="km-btn" onClick={seedItems} data-testid="rulebook-seed-btn">
                Seed worklist
              </button>
            )}
            <button className="km-btn" onClick={() => navigate('/admin/diagnostics')} data-testid="rulebook-open-diagnostics">
              <ShieldCheck className="w-4 h-4" /> Diagnostics
            </button>
          </div>
        </div>

        <div className="km-banner" data-testid="rulebook-maintenance-banner">
          <ShieldCheck className="w-5 h-5 km-banner-icon" />
          <div>
            <div className="km-banner-title">Maintenance Contract (Standing Rule)</div>
            <div className="km-banner-body">{rulebookData.maintenance_contract}</div>
          </div>
        </div>

        <div className="km-search">
          <Search className="w-4 h-4 km-search-icon" />
          <input
            className="km-input"
            placeholder="Search features, rules, collections, routes, files, worklist items…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="rulebook-search-input"
          />
        </div>

        <div className="km-tabs" role="tablist" data-testid="rulebook-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                data-active={tab === t.id}
                className="km-tab"
                onClick={() => setTab(t.id)}
                data-testid={t.testid}
              >
                <Icon className="w-4 h-4" /> {t.label}
                <span className="km-count" data-testid={t.countTestid}>{t.count}</span>
              </button>
            );
          })}
        </div>

        {/* App Map — default tab. Left rail is the tree; right pane is the node detail view. */}
        {tab === 'app-map' && (
          <div className="km-tabpanel">
            <div className="grid grid-cols-12 gap-4">
              <aside className="col-span-12 md:col-span-4" data-testid="rulebook-app-map-tree">
                <div className="km-card">
                  <div className="km-card-head--tight km-card-head">
                    <div className="km-eyebrow">Index by App Navigation</div>
                    <div className="km-card-desc">Mirrors the real sidebar + project navigation manifest.</div>
                  </div>
                  <div className="km-card-body--flush">
                    <div className="space-y-0.5">
                      {(rulebookMap.top_level || []).filter(n => nodeMatches(n, query) || MAP_FLAT.some(x => x.id.startsWith(n.id + '.') && nodeMatches(x, query))).map(top => (
                        <MapTreeNode
                          key={top.id}
                          node={top}
                          depth={0}
                          expanded={expanded}
                          onToggle={toggleExpanded}
                          selectedNodeId={selectedNodeId}
                          onSelect={setSelectedNodeId}
                          remainingItemsCountByNode={remainingItemsCountByNode}
                          query={query}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
              <main className="col-span-12 md:col-span-8" data-testid={`rulebook-app-map-node-${selectedNode.id}`}>
                <div className="km-card">
                  <div className="km-card-head">
                    {/* Breadcrumbs */}
                    <div className="km-crumbs" data-testid="rulebook-node-breadcrumbs">
                      {selectedNode.path.map((label, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <ChevronRight className="w-3 h-3" />}
                          <span className={i === selectedNode.path.length - 1 ? 'km-crumbs-current' : undefined}>
                            {label}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-1 flex-wrap gap-2">
                      <h2 className="km-card-title">{selectedNode.label}</h2>
                      {(remainingItemsCountByNode.get(selectedNode.id) || 0) > 0 && (
                        <span className="km-tag km-tag--remaining">
                          {remainingItemsCountByNode.get(selectedNode.id)} remaining
                        </span>
                      )}
                    </div>
                    {selectedNode.route && (
                      <div className="km-route">route: <code>{selectedNode.route}</code></div>
                    )}
                  </div>
                  <div className="km-card-body space-y-5">
                    {selectedNode.description && (
                      <section>
                        <h3 className="km-eyebrow">Plain English</h3>
                        <p className="km-prose km-prose--strong">{selectedNode.description}</p>
                      </section>
                    )}
                    {(selectedNode.feature_ids || []).length > 0 && (
                      <section>
                        <h3 className="km-eyebrow">Backed by (engineering view)</h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedNode.feature_ids.map(fid => {
                            const f = featureById(fid);
                            if (!f) return null;
                            return (
                              <button
                                key={fid}
                                onClick={() => { setSelectedFeatureId(fid); setTab('features'); }}
                                className="km-chip"
                                data-testid={`rulebook-node-feature-link-${fid}`}
                              >
                                {f.name} <ChevronRight className="w-3 h-3" />
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    )}
                    {(selectedNode.children || []).length > 0 && (
                      <section>
                        <h3 className="km-eyebrow">Under this section</h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedNode.children.map(c => (
                            <button
                              key={c.id}
                              onClick={() => setSelectedNodeId(c.id)}
                              className="km-chip"
                              data-testid={`rulebook-node-child-link-${c.id}`}
                            >
                              {c.label} <ChevronRight className="w-3 h-3" />
                            </button>
                          ))}
                        </div>
                      </section>
                    )}
                    {selectedNode.manifest_section && (
                      <section>
                        <h3 className="km-eyebrow">Manifest section</h3>
                        <div className="km-prose">
                          Populated at runtime from{' '}
                          <code className="km-code">PROJECT_NAV_SECTIONS[{JSON.stringify(selectedNode.manifest_section)}]</code>
                          <ul className="km-list mt-1">
                            {manifestSectionItems(selectedNode.manifest_section).map((it, idx) => (
                              <li key={idx}>
                                {it.label} <span className="km-meta">{it.value ? `tab: ${it.value}` : it.route}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </section>
                    )}
                    {/* Per-node Fixes & Additions */}
                    <section data-testid={`rulebook-node-worklist-${selectedNode.id}`}>
                      <h3 className="km-eyebrow">
                        <AlertTriangle className="w-3 h-3" /> Fixes &amp; Additions on this screen
                      </h3>
                      <div className="space-y-3">
                        <WorklistCaptureForm
                          homeFeatureId={(selectedNode.feature_ids || [])[0] || null}
                          allFeatures={rulebookData.features}
                          onCreated={onItemCreated}
                        />
                        {itemsForSelectedNode.length === 0 && (
                          <div className="km-empty">
                            {(selectedNode.feature_ids || []).length === 0
                              ? 'No engineering feature is xref\'d against this node yet — items captured here land as New / Unassigned in the aggregate tab.'
                              : 'No items yet for the linked feature(s). Capture one above.'}
                          </div>
                        )}
                        {itemsForSelectedNode.map(it => (
                          <WorklistItem
                            key={it.id}
                            item={it}
                            featureById={featureById}
                            isSuperAdmin={isSuperAdmin}
                            onChanged={onItemChanged}
                            showHomeChip
                          />
                        ))}
                      </div>
                    </section>
                  </div>
                </div>
              </main>
            </div>
          </div>
        )}

        {/* Features */}
        {tab === 'features' && (
          <div className="km-tabpanel">
            <div className="grid grid-cols-12 gap-4">
              <aside className="col-span-12 md:col-span-3" data-testid="rulebook-feature-index">
                <div className="km-card">
                  <div className="km-card-head km-card-head--tight">
                    <div className="km-eyebrow">Index by Feature</div>
                  </div>
                  <div className="km-card-body--flush">
                    <div className="flex flex-col">
                      {filteredFeatures.map(f => {
                        const featureRemaining = remainingItemsCountByFeature.get(f.id) || 0;
                        return (
                          <button
                            key={f.id}
                            onClick={() => setSelectedFeatureId(f.id)}
                            className="km-tree-row km-tree-label flex items-center justify-between gap-2"
                            data-selected={selectedFeatureId === f.id}
                            data-testid={`rulebook-feature-link-${f.id}`}
                          >
                            <span>{f.name}</span>
                            {featureRemaining > 0 && (
                              <span className="km-tag km-tag--remaining">{featureRemaining}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </aside>

              <main className="col-span-12 md:col-span-9" data-testid={`rulebook-feature-detail-${selectedFeature.id}`}>
                <div className="km-card">
                  <div className="km-card-head">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h2 className="km-card-title">{selectedFeature.name}</h2>
                      {(remainingItemsCountByFeature.get(selectedFeature.id) || 0) > 0 && (
                        <span className="km-tag km-tag--remaining">
                          {remainingItemsCountByFeature.get(selectedFeature.id)} remaining
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="km-card-body space-y-6">
                    <section data-testid="rulebook-layer-plain-english">
                      <h3 className="km-eyebrow"><BookOpen className="w-3 h-3" /> Plain English</h3>
                      <p className="km-prose km-prose--strong">{selectedFeature.plain_english}</p>
                    </section>

                    <section data-testid="rulebook-layer-data">
                      <h3 className="km-eyebrow"><Database className="w-3 h-3" /> The Data</h3>
                      <div className="space-y-2">
                        <div className="km-prose">
                          <span className="km-prose--strong">Single source of truth: </span>
                          <code className="km-code">{selectedFeature.data?.single_source_of_truth || '—'}</code>
                        </div>
                        {selectedFeature.data?.derivatives?.length > 0 && (
                          <div>
                            <div className="km-prose km-prose--strong mb-1">Derivatives:</div>
                            <ul className="km-list list-disc list-inside space-y-1">
                              {selectedFeature.data.derivatives.map((d, i) => <li key={i}>{d}</li>)}
                            </ul>
                          </div>
                        )}
                        {selectedFeature.data?.collections?.length > 0 && (
                          <div>
                            <div className="km-prose km-prose--strong mb-1">Collections:</div>
                            <ul className="km-list space-y-1" style={{ listStyle: 'none', paddingLeft: 0 }}>
                              {selectedFeature.data.collections.map((c, i) => (
                                <li key={i}>
                                  <code className="km-code">{c.name}</code>
                                  <span className="ml-2">— {c.role}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </section>

                    <section data-testid="rulebook-layer-rules">
                      <h3 className="km-eyebrow"><ListChecks className="w-3 h-3" /> The Rules (invariants)</h3>
                      <ul className="space-y-2" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {selectedFeature.rules.map((r, i) => (
                          <li key={i} className="km-rule-line">
                            <span className="km-rule-bullet">▸</span>
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </section>

                    <section data-testid="rulebook-layer-code">
                      <h3 className="km-eyebrow"><FileCode className="w-3 h-3" /> The Code</h3>
                      {selectedFeature.code?.backend_routes?.length > 0 && (
                        <div className="mb-3">
                          <div className="km-eyebrow">Backend routes</div>
                          <div className="space-y-1">
                            {selectedFeature.code.backend_routes.map((r, i) => (
                              <div key={i} className="km-routerow">
                                <span className="km-tag">{r.method}</span>
                                <code>{r.path}</code>
                                <span className="km-routerow-file">{r.file}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedFeature.code?.frontend_files?.length > 0 && (
                        <div>
                          <div className="km-eyebrow">Frontend files</div>
                          <ul className="km-filelist space-y-0.5">
                            {selectedFeature.code.frontend_files.map((f, i) => <li key={i}>· {f}</li>)}
                          </ul>
                        </div>
                      )}
                    </section>

                    {/* Per-feature Fixes & Additions */}
                    <section data-testid={`rulebook-feature-worklist-${selectedFeature.id}`}>
                      <h3 className="km-eyebrow">
                        <AlertTriangle className="w-3 h-3" /> Fixes &amp; Additions on this feature
                      </h3>
                      <div className="space-y-3">
                        <WorklistCaptureForm
                          homeFeatureId={selectedFeature.id}
                          allFeatures={rulebookData.features}
                          onCreated={onItemCreated}
                        />
                        {loadingItems && <div className="km-loading">Loading…</div>}
                        {!loadingItems && itemsForSelectedFeature.length === 0 && (
                          <div className="km-empty">No items yet. Capture one above.</div>
                        )}
                        {itemsForSelectedFeature.map(it => (
                          <div key={it.id}>
                            {it.feature_id !== selectedFeature.id && (
                              <div className="km-meta mb-1">
                                Affected by → home: {featureById(it.feature_id)?.name || '(unassigned)'}
                              </div>
                            )}
                            <WorklistItem
                              item={it}
                              featureById={featureById}
                              isSuperAdmin={isSuperAdmin}
                              onChanged={onItemChanged}
                            />
                          </div>
                        ))}
                      </div>
                    </section>
                  </div>
                </div>
              </main>
            </div>
          </div>
        )}

        {/* Data Types */}
        {tab === 'data-types' && (
          <div className="km-tabpanel">
            <div className="grid grid-cols-12 gap-4">
              <aside className="col-span-12 md:col-span-3" data-testid="rulebook-datatype-index">
                <div className="km-card">
                  <div className="km-card-head km-card-head--tight">
                    <div className="km-eyebrow">Index by Data Type</div>
                  </div>
                  <div className="km-card-body--flush">
                    <div className="flex flex-col">
                      {filteredTypes.map(t => (
                        <button
                          key={t.id}
                          onClick={() => setSelectedTypeId(t.id)}
                          className="km-tree-row km-tree-label"
                          data-selected={selectedTypeId === t.id}
                          data-testid={`rulebook-datatype-link-${t.id}`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
              <main className="col-span-12 md:col-span-9" data-testid={`rulebook-datatype-detail-${selectedType.id}`}>
                <div className="km-card">
                  <div className="km-card-head">
                    <h2 className="km-card-title">{selectedType.name}</h2>
                    <div className="km-card-desc">{selectedType.description}</div>
                  </div>
                  <div className="km-card-body space-y-5">
                    <section>
                      <h3 className="km-eyebrow">Collections</h3>
                      <ul className="space-y-1" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {(selectedType.collections || []).map((c, i) => (
                          <li key={i}><code className="km-code">{c}</code></li>
                        ))}
                      </ul>
                    </section>
                    <section>
                      <h3 className="km-eyebrow">Used by features</h3>
                      <div className="flex flex-wrap gap-2">
                        {(selectedType.features || []).map(fid => {
                          const f = featureById(fid);
                          if (!f) return null;
                          return (
                            <button
                              key={fid}
                              onClick={() => { setSelectedFeatureId(fid); setTab('features'); }}
                              className="km-chip"
                              data-testid={`rulebook-datatype-crosslink-${fid}`}
                            >
                              {f.name} <ChevronRight className="w-3 h-3" />
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  </div>
                </div>
              </main>
            </div>
          </div>
        )}

        {/* Aggregate Fixes & Additions */}
        {tab === 'fixes-additions' && (
          <div className="km-tabpanel">
            <div className="km-card">
              <div className="km-card-head">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h2 className="km-card-title">Fixes &amp; Additions</h2>
                    <div className="km-card-desc">
                      One list, pooled across all features. Blocking items are pinned at the top.
                    </div>
                  </div>
                  <button className="km-btn" onClick={copyOpenItems} data-testid="worklist-copy-open">
                    <ClipboardCopy className="w-4 h-4" /> Copy open items
                  </button>
                </div>
              </div>
              <div className="km-card-body space-y-3">
                {/* Filter row */}
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    value={aggFilter.status}
                    onChange={(e) => setAggFilter(f => ({ ...f, status: e.target.value }))}
                    className="km-select km-input--inline"
                    data-testid="worklist-filter-status"
                  >
                    <option value="all">All statuses</option>
                    <option value="open">Open</option>
                    <option value="prompted">Prompted</option>
                    <option value="built">Built — not yet confirmed</option>
                    <option value="verified">Completed</option>
                    <option value="withdrawn">Not doing</option>
                  </select>
                  <select
                    value={aggFilter.featureId}
                    onChange={(e) => setAggFilter(f => ({ ...f, featureId: e.target.value }))}
                    className="km-select km-input--inline"
                    data-testid="worklist-filter-feature"
                  >
                    <option value="">Any feature</option>
                    {rulebookData.features.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                  <label className="km-checkline">
                    <input
                      type="checkbox"
                      checked={aggFilter.blockingOnly}
                      onChange={(e) => setAggFilter(f => ({ ...f, blockingOnly: e.target.checked }))}
                      data-testid="worklist-filter-blocking"
                    />
                    Blocking only
                  </label>
                  <div className="ml-auto km-meta" data-testid="worklist-counts">
                    {aggregateItems.length} shown ·{' '}
                    <span data-testid="worklist-remaining-count">{remainingCount} remaining</span>{' '}
                    · <span data-testid="worklist-completed-count">{completedCount} completed</span>
                  </div>
                </div>

                {/* New / Unassigned capture */}
                <div>
                  <div className="km-eyebrow">Capture into any feature — or New / Unassigned</div>
                  <WorklistCaptureForm
                    homeFeatureId={null}
                    allFeatures={rulebookData.features}
                    onCreated={onItemCreated}
                  />
                </div>

                {/* Items */}
                {loadingItems && <div className="km-loading">Loading…</div>}
                {aggregateItems.map(it => (
                  <WorklistItem
                    key={it.id}
                    item={it}
                    featureById={featureById}
                    isSuperAdmin={isSuperAdmin}
                    onChanged={onItemChanged}
                    showHomeChip
                  />
                ))}
                {!loadingItems && aggregateItems.length === 0 && (
                  <div className="km-empty">No items match. Try clearing the filter or capture one above.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Recursive tree node — indented with ChevronDown/Right for expandable
 * parents, sits at the left rail of the App Map tab. */
function MapTreeNode({ node, depth, expanded, onToggle, selectedNodeId, onSelect, remainingItemsCountByNode, query }) {
  const hasChildren = (node.children || []).length > 0;
  const isOpen = expanded.has(node.id);
  const isSelected = selectedNodeId === node.id;
  const remainingCount = remainingItemsCountByNode.get(node.id) || 0;

  // While filtering, force-open parents whose subtree contains matches.
  const forceOpen = !!query && hasChildren && MAP_FLAT.some(x => x.id.startsWith(node.id + '.') && nodeMatches(x, query));
  const effectiveOpen = isOpen || forceOpen;

  return (
    <div>
      <div
        className="km-tree-row"
        data-selected={isSelected}
        style={{ paddingLeft: `calc(var(--km-s2) + ${depth} * var(--km-s3))` }}
      >
        {hasChildren ? (
          <button
            onClick={() => onToggle(node.id)}
            className="km-tree-toggle"
            data-testid={`rulebook-tree-toggle-${node.id}`}
            aria-label={effectiveOpen ? 'Collapse' : 'Expand'}
          >
            {effectiveOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : (
          <span className="w-5" />
        )}
        <button
          onClick={() => onSelect(node.id)}
          className="km-tree-label"
          data-testid={`rulebook-tree-node-${node.id}`}
        >
          {node.label}
        </button>
        {remainingCount > 0 && (
          <span className="km-tag km-tag--remaining mr-2">{remainingCount}</span>
        )}
      </div>
      {hasChildren && effectiveOpen && (
        <div>
          {node.children
            .filter(c => nodeMatches(c, query) || MAP_FLAT.some(x => x.id.startsWith(c.id + '.') && nodeMatches(x, query)))
            .map(c => (
              <MapTreeNode
                key={c.id}
                node={c}
                depth={depth + 1}
                expanded={expanded}
                onToggle={onToggle}
                selectedNodeId={selectedNodeId}
                onSelect={onSelect}
                remainingItemsCountByNode={remainingItemsCountByNode}
                query={query}
              />
            ))}
        </div>
      )}
    </div>
  );
}
